/** Countries a merchant can sign up in from the public signup page. */
export type SignupCountry = 'SE' | 'NO' | 'FI' | 'DK';

export const signupCountries: SignupCountry[] = ['SE', 'NO', 'FI', 'DK'];

export const isSignupCountry = (value: string | null | undefined): value is SignupCountry =>
    signupCountries.includes((value ?? '').toUpperCase() as SignupCountry);

/** GET merchant-signup/companies/{countryCode}/{organizationNumber} */
export interface CompanyLookupRequest {
    country: SignupCountry;
    organizationNumber: string;
}

export interface CompanyAddress {
    street: string;
    postalCode: string;
    city: string;
}

/**
 * A company as the registry reports it. Any of address, website, phone and email is null when the registry lacks it.
 * The name is null only outside production, where the API answers a company the registry does not know with an empty
 * company; the user then enters the name.
 */
export interface CompanyLookup {
    companyName: string | null;
    address: CompanyAddress | null;
    website: string | null;
    phone: string | null;
    email: string | null;
}

/** name is the registry's name, or the one the user entered when the registry has none; the API prefers the registry's. */
export interface SignupCompany {
    name: string;
    address: CompanyAddress;
    website: string;
    phone: string;
    email: string;
}

export interface SignupHubUser {
    name: string;
    phone: string;
    email: string;
}

/**
 * POST merchant-signup: the company by country and organization number, its details as the user edited them and
 * the person who gets Merchant Hub access.
 * key is the value a prefilled signup link carried, so the signup can be tied to e.g. a sales lead.
 */
export interface SignupRequest {
    countryCode: SignupCountry;
    organizationNumber: string;
    company: SignupCompany;
    hubUser: SignupHubUser;
    key?: string;
}

/**
 * The fields of the signup request by their path, as the API names them in a 422 field error, listed in the order
 * the signup form shows them.
 */
export const signupFieldPaths = [
    'company.name',
    'company.address.street',
    'company.address.postalCode',
    'company.address.city',
    'company.website',
    'company.phone',
    'company.email',
    'hubUser.name',
    'hubUser.phone',
    'hubUser.email',
] as const;

export type SignupFieldPath = (typeof signupFieldPaths)[number];

export const isSignupFieldPath = (name: string): name is SignupFieldPath =>
    signupFieldPaths.includes(name as SignupFieldPath);
